import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DeletedFilter,
  RestoreAction,
} from "../../components/common/DeletedFilter";
import {
  ADMIN_USER_LIST,
  ADMIN_LOGS,
  COUNTRY_LIST_DROPDOWN,
  STATE_LIST_DROPDOWN,
  CITY_LIST_DROPDOWN,
  REGION_LIST_DROPDOWN,
  CHAPTER_LIST_DROPDOWN,
} from "../../routes/routes";
import { PAGINATE } from "../../constant/constant";
import { toast } from "react-toastify";
import {
  NoDataFound,
  Pagination,
  hasRole,
  checkEmptyOrNull,
} from "../../components/Utils";
import Loader from "../../components/common/Loader";
import { Link, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { usePermission } from "../../hooks/usePermission";
import StickyTable from "../../components/common/StickyTable";
import {
  ADMIN_TYPES,
  GEO_LEVEL_FILTER_ROLES,
  GEO_LEVEL_META,
  getAllowedAdminTypes,
  getCreatableAdminTypes,
} from "./adminTypeConfig";

const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const AdminList = () => {
  const { hasPermission } = usePermission();
  const navigate = useNavigate();

  const allowedTypes = useMemo(
    () => getAllowedAdminTypes(hasPermission, hasRole),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const creatableTypes = useMemo(
    () => getCreatableAdminTypes(hasPermission, hasRole),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const allowedTypeKeys = allowedTypes.map((t) => t.key).join(",");

  const typeOptions = useMemo(
    () => [
      { value: "", label: "Select" },
      ...allowedTypes.map((t) => ({
        value: t.key,
        label: t.label,
      })),
    ],
    [allowedTypes]
  );

  const [adminTypeKey, setAdminTypeKey] = useState(null);
  const config = adminTypeKey ? ADMIN_TYPES[adminTypeKey] : null;
  const createPath = adminTypeKey
    ? `/admins/create?type=${adminTypeKey}`
    : "/admins/create";

  const [data, setData] = useState([]);
  const [paginator, setPaginator] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [refresh, setRefresh] = useState(false);
  const [confirmation, setConfirmation] = useState({});
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [detailsModal, setDetailModal] = useState(false);
  const [detailData, setDetailData] = useState({});
  const [filter, setFilter] = useState({});
  const [deletedFilter, setDeletedFilter] = useState("no");

  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);

  const authUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const isSuperAdmin = Number(authUser?.user_type) === 1;

  const [logAdmin, setLogAdmin] = useState(null);
  const [logModal, setLogModal] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [logData, setLogData] = useState([]);

  const fetchAdminLogs = async (adminUser) => {
    setLogAdmin(adminUser);
    setLogModal(true);
    setLogLoading(true);
    try {
      const res = await axios.get(ADMIN_LOGS + adminUser.id);
      if (res.status === 200 && res.data.success) {
        setLogData(res.data.data || []);
      } else {
        setLogData([]);
      }
    } catch (error) {
      console.error(error);
      setLogData([]);
      toast.error(error?.response?.data?.msg || "Failed to load activity logs");
    } finally {
      setLogLoading(false);
    }
  };

  const selectStateRef = useRef(null);
  const selectCityRef = useRef(null);
  const selectRegionRef = useRef(null);
  const selectChapterRef = useRef(null);

  const clearSelect = (ref) => {
    if (ref.current?.clearValue) ref.current.clearValue();
  };

  const handleStateClear = () => {
    clearSelect(selectStateRef);
    clearSelect(selectCityRef);
    clearSelect(selectRegionRef);
    clearSelect(selectChapterRef);
  };

  const handleCityClear = () => {
    clearSelect(selectCityRef);
    clearSelect(selectRegionRef);
    clearSelect(selectChapterRef);
  };

  const handleRegionClear = () => {
    clearSelect(selectRegionRef);
    clearSelect(selectChapterRef);
  };

  const handleChapterClear = () => {
    clearSelect(selectChapterRef);
  };

  const activeGeoLevels = useMemo(() => {
    if (config) {
      if (
        config.access?.level &&
        !config.geoLevels.includes(config.access.level)
      ) {
        return [...config.geoLevels, config.access.level];
      }
      return config.geoLevels;
    }
    return ["country", "state", "city", "region", "chapter"];
  }, [config]);

  const showGeoLevel = (level) =>
    activeGeoLevels.includes(level) && hasRole(GEO_LEVEL_FILTER_ROLES[level]);

  const visibleGeoLevels = useMemo(
    () => activeGeoLevels.filter((level) => hasRole(GEO_LEVEL_FILTER_ROLES[level])),
    [activeGeoLevels]
  );

  const tableGeoLevels = useMemo(() => {
    return activeGeoLevels.filter((level) =>
      hasRole(GEO_LEVEL_FILTER_ROLES[level])
    );
  }, [activeGeoLevels]);

  const colSpan = 6 + tableGeoLevels.length + 1; // sno, name, email, mobile, admin_type, role + geos + action

  const getRowConfig = (row) =>
    ADMIN_TYPES[row?.admin_type || row?._adminTypeKey] || config || null;

  const getGeoCellValue = (row, level) => {
    const rowConfig = getRowConfig(row);
    if (rowConfig?.access?.level === level) {
      return row?.access_names || "-";
    }
    return row?.[GEO_LEVEL_META[level]?.columnKey] || "-";
  };

  const changeAdminType = (selected) => {
    const key = selected?.value ? selected.value : null;
    if (key === adminTypeKey) return;
    setAdminTypeKey(key);
    setPage(1);
    setFilter({});
    setDeletedFilter("no");
    setData([]);
    setPaginator({});
    handleStateClear();
  };

  const HandleDelete = async (userId, rowConfig) => {
    if (!rowConfig) return;
    try {
      axios
        .delete(rowConfig.api.delete + userId, {
          params: { admin_type: rowConfig.key },
        })
        .then((res) => {
          if (res.status === 200 && res.data.success) {
            toast.success(rowConfig.toasts.deleted);
            setRefresh((prev) => !prev);
            navigate("/admins");
          }
        })
        .catch((err) => {
          if (err.response?.status === 422) {
            toast.error(err.response.data.msg);
          }
        });
    } catch (error) {
      console.error(error);
    }
  };

  const HandleRestore = async (recordId, rowConfig) => {
    if (!rowConfig) return;
    try {
      axios
        .post(
          rowConfig.api.restore + recordId,
          {},
          { params: { admin_type: rowConfig.key } }
        )
        .then((res) => {
          if (res.status === 200 && res.data.success) {
            toast.success("Restored successfully");
            setRefresh((prev) => !prev);
          } else {
            toast.error(res?.data?.msg || "Failed to restore");
          }
        })
        .catch((err) => {
          toast.error(err.response?.data?.msg || "Failed to restore");
        });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (allowedTypes.length === 0) {
      setLoading(false);
      setData([]);
      setPaginator({});
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchList = async () => {
      try {
        const response = await axios.get(ADMIN_USER_LIST, {
          params: {
            type: PAGINATE,
            page,
            perPage,
            ...filter,
            deleted: deletedFilter,
            ...(config ? { admin_type: config.key } : {}),
          },
        });
        if (cancelled) return;
        if (response.status === 200 && response.data.success) {
          setData(
            (response.data.data.users || []).map((u) => ({
              ...u,
              _adminTypeKey: u.admin_type || config?.key,
            }))
          );
          setPaginator({
            prev: !!response.data.data.prev,
            next: !!response.data.data.next,
            total: response.data.data.total,
            current: response.data.data.current_page,
            last: response.data.data.last_page,
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const fetchDropdowns = async () => {
      try {
        if (activeGeoLevels.includes("country")) {
          const countryResponse = await axios.get(COUNTRY_LIST_DROPDOWN);
          setCountryOptions(countryResponse.data.data || []);
        }
        if (activeGeoLevels.includes("state")) {
          const stateResponse = await axios.get(STATE_LIST_DROPDOWN, {
            params: { ...filter },
          });
          setStateOptions(stateResponse.data.data || []);
        }
        if (activeGeoLevels.includes("city")) {
          const cityResponse = await axios.get(CITY_LIST_DROPDOWN, {
            params: { ...filter },
          });
          setCityOptions(cityResponse.data.data || []);
        }
        if (activeGeoLevels.includes("region")) {
          const regionResponse = await axios.get(REGION_LIST_DROPDOWN, {
            params: { ...filter },
          });
          setRegionOptions(regionResponse.data.data || []);
        }
        if (activeGeoLevels.includes("chapter")) {
          const chapterResponse = await axios.get(CHAPTER_LIST_DROPDOWN, {
            params: { ...filter },
          });
          setChapterOptions(chapterResponse.data.data || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchList();
    fetchDropdowns();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, page, filter, perPage, refresh, deletedFilter, allowedTypeKeys]);

  if (allowedTypes.length === 0) {
    return (
      <div className="content-wrapper">
        <div className="card icard p-4">
          <NoDataFound colspan="1" message="No admin types available" />
        </div>
      </div>
    );
  }

  const optionsByLevel = {
    country: countryOptions,
    state: stateOptions,
    city: cityOptions,
    region: regionOptions,
    chapter: chapterOptions,
  };

  const refByLevel = {
    state: selectStateRef,
    city: selectCityRef,
    region: selectRegionRef,
    chapter: selectChapterRef,
  };

  const onGeoFilterChange = (level, selected) => {
    if (level === "country") {
      handleStateClear();
      setFilter({
        ...filter,
        country_id: selected?.value,
        state_id: null,
        city_id: null,
        region_id: null,
        chapter_id: null,
      });
      return;
    }
    if (level === "state") {
      handleCityClear();
      setFilter({
        ...filter,
        state_id: selected?.value,
        city_id: null,
        region_id: null,
        chapter_id: null,
      });
      return;
    }
    if (level === "city") {
      handleRegionClear();
      setFilter({
        ...filter,
        city_id: selected?.value,
        region_id: null,
        chapter_id: null,
      });
      return;
    }
    if (level === "region") {
      handleChapterClear();
      setFilter({
        ...filter,
        region_id: selected?.value,
        chapter_id: null,
      });
      return;
    }
    if (level === "chapter") {
      setFilter({
        ...filter,
        chapter_id: selected?.value,
      });
    }
  };

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-2">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Admins</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>
                        <span>Dashboard</span>
                      </Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">Admins List</span>
                </li>
              </ol>
            </div>
            <div className="col-lg-6">
              <div className="pt-btn">
                {creatableTypes.length > 0 &&
                  (!config || hasPermission(config.permissions.create)) && (
                    <Link to={createPath}>
                      <button
                        type="button"
                        className="btn rounded-pill btn-primary ibutton"
                      >
                        <span className="tf-icons bx bx-plus"></span>&nbsp; Add
                      </button>
                    </Link>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-grow-1">
        <div className="card icard sticky-table-card">
          <div className="container-fluid f_containter">
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="mb-3">
                  <label className="form-label" htmlFor="admin_type">
                    Admin Type
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={selectPortalStyles}
                    name="admin_type"
                    isClearable
                    options={typeOptions}
                    placeholder="Select"
                    value={
                      adminTypeKey
                        ? typeOptions.find((o) => o.value === adminTypeKey)
                        : typeOptions.find((o) => o.value === "")
                    }
                    onChange={changeAdminType}
                  />
                </div>
              </div>

              {activeGeoLevels.map((level) => {
                if (!showGeoLevel(level)) return null;
                const meta = GEO_LEVEL_META[level];
                return (
                  <div className="col-md-3" key={level}>
                    <div className="mb-3">
                      <label className="form-label" htmlFor={meta.filterKey}>
                        {meta.label}
                      </label>
                      <Select
                        menuPortalTarget={document.body}
                        styles={selectPortalStyles}
                        ref={refByLevel[level] || undefined}
                        name={meta.filterKey}
                        isClearable
                        options={optionsByLevel[level]}
                        onChange={(selected) =>
                          onGeoFilterChange(level, selected)
                        }
                      />
                    </div>
                  </div>
                );
              })}

              <div className="col-md-3">
                <div className="mb-3">
                  <label className="form-label" htmlFor="search">
                    Search
                  </label>
                  <input
                    key={`search-${adminTypeKey || "all"}`}
                    type="text"
                    className="form-control hide-arrow"
                    id="search"
                    placeholder="Search by Name, Email, Mobile"
                    onChange={(e) =>
                      setFilter({ ...filter, search: e.target.value })
                    }
                  />
                </div>
              </div>

              <DeletedFilter
                value={deletedFilter}
                onChange={(value) => {
                  setPage(1);
                  setDeletedFilter(value);
                }}
              />
            </div>
          </div>

          {data.length > 0 && (
            <Pagination
              paginator={paginator}
              page={(no) => setPage(no)}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}
          <StickyTable refreshDeps={[data, loading, adminTypeKey]}>
            <table className="table table-striped itable">
              <thead>
                <tr>
                  <th>Sl. No.</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Admin Type</th>
                  <th>Roles and Permission</th>
                  {tableGeoLevels.map((level) => (
                    <th key={level}>{GEO_LEVEL_META[level].label}</th>
                  ))}
                  <th style={{ textAlign: "center", width: "10%" }}>Action</th>
                </tr>
              </thead>
              <tbody className="table-border-bottom-0">
                {loading ? (
                  <Loader loading={loading} colspan={String(colSpan)} />
                ) : data.length > 0 ? (
                  data.map((user, i) => {
                    const rowConfig = getRowConfig(user);
                    return (
                      <tr key={`${user?._adminTypeKey || "x"}-${user?.id || i}`}>
                        <td>{user?.sno}</td>
                        <td>{checkEmptyOrNull(user?.name)}</td>
                        <td>{checkEmptyOrNull(user?.email)}</td>
                        <td>
                          {user?.mobile_number
                            ? `+${user?.mobile_country_code || "91"} ${user.mobile_number}`
                            : "-"}
                        </td>
                        <td>
                          {checkEmptyOrNull(
                            rowConfig?.label || user?.admin_type
                          )}
                        </td>
                        <td>{checkEmptyOrNull(user?.admin_role_name)}</td>
                        {tableGeoLevels.map((level) => (
                          <td key={level}>
                            {checkEmptyOrNull(getGeoCellValue(user, level))}
                          </td>
                        ))}
                        <td style={{ textAlign: "right" }}>
                          {deletedFilter === "yes" ? (
                            rowConfig &&
                            hasPermission(rowConfig.permissions.edit) && (
                              <RestoreAction
                                onClick={() => {
                                  setConfirmationModal(true);
                                  setConfirmation({
                                    message: "Are you sure you want to restore?",
                                    fn: () => HandleRestore(user?.id, rowConfig),
                                  });
                                }}
                              />
                            )
                          ) : (
                            <>
                              <button
                                className="badge bg-label-warning me-1 border-0"
                                data-bs-toggle="tooltip"
                                data-bs-offset="0,4"
                                data-bs-placement="top"
                                data-bs-html="true"
                                title="Activity History"
                                data-bs-original-title="Activity History"
                                onClick={() => fetchAdminLogs(user)}
                              >
                                <i className="bx bx-history"></i>
                              </button>
                              {rowConfig &&
                                hasPermission(rowConfig.permissions.list) && (
                                  <button
                                    className="badge bg-label-info me-1 border-0"
                                    data-bs-toggle="tooltip"
                                    data-bs-offset="0,4"
                                    data-bs-placement="top"
                                    data-bs-html="true"
                                    title="View"
                                    data-bs-original-title="View"
                                    onClick={() => {
                                      setDetailModal(true);
                                      setDetailData({
                                        ...user,
                                        admin_type_label: rowConfig.label,
                                        access_label: rowConfig.access?.label || "Assigned Access",
                                      });
                                    }}
                                  >
                                    <i className="bx bx-show"></i>
                                  </button>
                                )}
                              {rowConfig &&
                                hasPermission(rowConfig.permissions.edit) && (
                                  <Link to={rowConfig.paths.edit(user.id)}>
                                    <button
                                      className="badge bg-label-success me-1 border-0"
                                      data-bs-toggle="tooltip"
                                      data-bs-offset="0,4"
                                      data-bs-placement="top"
                                      data-bs-html="true"
                                      title="Edit"
                                      data-bs-original-title="Edit"
                                    >
                                      <i className="bx bx-pencil"></i>
                                    </button>
                                  </Link>
                                )}
                              {rowConfig &&
                                hasPermission(rowConfig.permissions.delete) && (
                                  <button
                                    onClick={() => {
                                      setConfirmationModal(true);
                                      setConfirmation({
                                        message:
                                          "Are you sure, you want to delete?",
                                        fn: () => HandleDelete(user?.id, rowConfig),
                                      });
                                    }}
                                    className="badge bg-label-danger me-1 border-0"
                                    data-bs-toggle="tooltip"
                                    data-bs-offset="0,4"
                                    data-bs-placement="top"
                                    data-bs-html="true"
                                    title="Delete"
                                    data-bs-original-title="Delete"
                                  >
                                    <i className="bx bx-trash"></i>
                                  </button>
                                )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <NoDataFound colspan={String(colSpan)} message="No Data Found" />
                )}
              </tbody>
            </table>
          </StickyTable>
          {data.length > 0 && (
            <Pagination
              paginator={paginator}
              page={(no) => setPage(no)}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}
        </div>
      </div>

      <Modal
        show={confirmationModal}
        size="sm"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        onHide={() => setConfirmationModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton className="no-border"></Modal.Header>
        <Modal.Body className="idel_popup">
          <div className="icon_holder">
            <div className="pop_del_icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="50"
                height="50"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#CF2030"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </div>
          </div>
          <h6 className="text-center modal_title">{confirmation?.message}</h6>
          <div className="text-center">
            <button
              onClick={() => {
                setConfirmationModal(false);
                setConfirmation({});
              }}
              className="btn btn-danger btn-sm me-2 iclose_btn"
            >
              No
            </button>
            <button
              onClick={() => {
                confirmation.fn();
                setConfirmationModal(false);
                setConfirmation({});
              }}
              className="btn btn-success btn-sm isubmit_button"
            >
              Yes
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={detailsModal}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        onHide={() => setDetailModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title
            id="contained-modal-title-vcenter"
            className="modal_title1"
          >
            Admin Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="card-body py-0">
            <div className="row">
              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">Admin Type</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.admin_type_label)}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">Roles and Permission</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.admin_role_name)}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">Name</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.name)}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">Email</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.email)}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">Mobile Number</label>
                  <span className="d-block font-weight-600">
                    {detailData?.mobile_number
                      ? `+${detailData?.mobile_country_code || "91"} ${detailData.mobile_number}`
                      : "-"}
                  </span>
                </div>
              </div>
              {["country", "state", "city", "region", "chapter"].map(
                (level) => {
                  const meta = GEO_LEVEL_META[level];
                  const value = getGeoCellValue(detailData, level);
                  if (!value || value === "-") return null;
                  return (
                    <div className="col-md-6" key={level}>
                      <div className="mb-4">
                        <label className="form-label m-0">{meta.label}</label>
                        <span className="d-block font-weight-600">
                          {checkEmptyOrNull(value)}
                        </span>
                      </div>
                    </div>
                  );
                }
              )}
              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">Created At</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.created_at)}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">Updated At</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.updated_at)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary ibutton"
            onClick={() => setDetailModal(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* Admin Activity Logs Modal */}
      <Modal
        show={logModal}
        size="xl"
        aria-labelledby="admin-activity-logs-modal"
        centered
        onHide={() => setLogModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title
            id="admin-activity-logs-modal"
            className="modal_title1 d-flex align-items-center"
          >
            <i className="bx bx-history me-2 text-warning fs-4"></i>
            Activity History &mdash;&nbsp;
            <span className="text-primary">{logAdmin?.name}</span>
            {getRowConfig(logAdmin)?.label && (
              <span
                className="badge bg-label-dark ms-2"
                style={{ fontSize: "11px" }}
              >
                {getRowConfig(logAdmin).label}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {logLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading activity logs...</p>
            </div>
          ) : logData.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-2">
                <i
                  className="bx bx-receipt text-muted"
                  style={{ fontSize: "48px" }}
                ></i>
              </div>
              <h6 className="text-muted">
                No activity logs recorded yet for this administrator.
              </h6>
              <p className="small text-muted mb-0">
                Role assignment modifications will appear here.
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "22%" }}>Date and time</th>
                    <th style={{ width: "26%" }}>Previously assigned role</th>
                    <th style={{ width: "26%" }}>Newly assigned role</th>
                    <th style={{ width: "26%" }}>created by</th>
                  </tr>
                </thead>
                <tbody>
                  {logData.map((log, idx) => {
                    const details =
                      typeof log.details === "string"
                        ? JSON.parse(log.details || "{}")
                        : log.details || {};
                    const prevRole =
                      details.previous_role_name ||
                      log.previous_role_name ||
                      "None";
                    const newRole =
                      log.role_name ||
                      log.new_role_name ||
                      details.new_role_name ||
                      "Assigned";

                    return (
                      <tr key={log.id || idx}>
                        <td className="text-nowrap">
                          <div className="d-flex align-items-center">
                            <i className="bx bx-calendar me-1 text-muted"></i>
                            <span style={{ fontSize: "12.5px" }}>
                              {log.created_at_time || log.created_at}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`badge ${prevRole && prevRole !== "None"
                                ? "bg-label-danger"
                                : "bg-label-secondary"
                              }`}
                            style={{ fontSize: "12px", fontWeight: "600" }}
                          >
                            {prevRole}
                          </span>
                        </td>
                        <td>
                          <span
                            className="badge bg-label-success"
                            style={{ fontSize: "12px", fontWeight: "600" }}
                          >
                            {newRole}
                          </span>
                        </td>
                        <td>
                          <div
                            className="fw-semibold text-dark"
                            style={{ fontSize: "13px" }}
                          >
                            {log.creator_name || "System"}
                          </div>
                          {log.creator_email && (
                            <div
                              className="fw-semibold text-secondary small"
                              style={{ fontSize: "11.5px" }}
                            >
                              {log.creator_email}
                            </div>
                          )}
                          <span
                            className="badge bg-label-primary mt-1"
                            style={{ fontSize: "10px" }}
                          >
                            {log.creator_level || "Admin"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary ibutton"
            onClick={() => setLogModal(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminList;
